/**
 * Sponsors — current sponsor of the month + logo prefetch.
 *
 * The backend returns each sponsor with a `logo_url`. For canvas
 * rasterization (PNG / animated WebM) the logo has to be a same-origin or
 * data-URL resource — otherwise CORS blocks it. We prefetch the logo and
 * convert it to a data URL, then hand it to the SVG builders as
 * `logo_data_url`. Data-URL logos (which is what the seed uses) are already
 * safe.
 */
import { api, endpoints } from "@/lib/api";

let cache = { at: 0, sponsors: null, current: null };
const CACHE_MS = 5 * 60 * 1000;

const fetchAsDataUrl = async (url) => {
    if (!url) return null;
    if (url.startsWith("data:")) return url;
    try {
        const r = await fetch(url, { mode: "cors" });
        if (!r.ok) return null;
        const blob = await r.blob();
        return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

export const fetchSponsors = async () => {
    if (cache.sponsors && Date.now() - cache.at < CACHE_MS) return cache.sponsors;
    try {
        const r = await api.get(endpoints.sponsors());
        cache = { at: Date.now(), sponsors: r.data, current: cache.current };
        return r.data;
    } catch {
        return [];
    }
};

export const fetchCurrentSponsor = async () => {
    try {
        const r = await api.get(endpoints.sponsorCurrent());
        const sponsor = r.data;
        const logo_data_url = await fetchAsDataUrl(sponsor.logo_url);
        return { ...sponsor, logo_data_url };
    } catch {
        return null;
    }
};

export const pickCurrentSponsor = (sponsors) => {
    if (!Array.isArray(sponsors) || sponsors.length === 0) return null;
    const month = new Date().getUTCMonth();
    return sponsors[month % sponsors.length];
};
